import React, { useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Grid,
  TextField,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { SearchOutlined, FilterAltOffOutlined } from "@mui/icons-material";

export interface CampoPesquisa<T = any> {
  campo: keyof T & string;
  label: string;
  // Largura do campo no grid de 12 colunas (telas médias em diante)
  md?: number;
}

interface ModalFiltroProps {
  aberto: boolean;
  titulo: string;
  campos: CampoPesquisa[];
  valores: Record<string, string>;
  onPesquisar: (filtros: Record<string, string>) => void;
  onLimpar: () => void;
  onFechar: () => void;
}

// Modal de pesquisa/filtro das listagens (mesmo padrão do XC WMS)
const ModalFiltro: React.FC<ModalFiltroProps> = ({
  aberto,
  titulo,
  campos,
  valores,
  onPesquisar,
  onLimpar,
  onFechar,
}) => {
  const valoresVazios = () =>
    campos.reduce<Record<string, string>>((acc, { campo }) => {
      acc[campo] = "";
      return acc;
    }, {});

  const { register, handleSubmit, reset } = useForm<Record<string, string>>({
    defaultValues: valoresVazios(),
  });

  // Ao abrir, carrega no formulário os filtros que estão aplicados
  useEffect(() => {
    if (aberto) {
      reset({ ...valoresVazios(), ...valores });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  const handlePesquisar = (dados: Record<string, string>) => {
    const filtros = Object.entries(dados).reduce<Record<string, string>>(
      (acc, [campo, valor]) => {
        if (valor && valor.trim()) acc[campo] = valor.trim();
        return acc;
      },
      {},
    );

    onPesquisar(filtros);
  };

  const handleLimparFiltros = () => {
    reset(valoresVazios());
    onLimpar();
  };

  return (
    <Dialog
      open={aberto}
      onClose={onFechar}
      fullWidth
      maxWidth={false}
      sx={{ "& .MuiDialog-paper": { width: "75%", maxWidth: "95%" } }}
    >
      <DialogTitle>Pesquisa de {titulo}</DialogTitle>
      <Box component="form" onSubmit={handleSubmit(handlePesquisar)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {campos.map(({ campo, label, md }, index) => (
              <Grid item xs={12} sm={6} md={md ?? 6} key={campo}>
                <TextField
                  {...register(campo)}
                  label={label}
                  variant="outlined"
                  size="small"
                  fullWidth
                  autoFocus={index === 0}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button variant="contained" color="warning" onClick={onFechar}>
            Voltar
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleLimparFiltros}
            startIcon={<FilterAltOffOutlined />}
          >
            Limpar Filtros
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SearchOutlined />}
            type="submit"
          >
            Pesquisar
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ModalFiltro;
